import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/joy';
import { loadGoogleMapsScript } from '../../../utils/loadGoogleMaps';
import type { RankingScanDetail, RankingGridPoint } from '../../../types/marketing';

interface RankingMapProps {
  scan: RankingScanDetail | null;
  centerLat: number;
  centerLng: number;
  loading?: boolean;
}

const RANK_COLORS = {
  top3: '#4CAF50',
  top10: '#FFC107',
  top20: '#FF9800',
  notRanking: '#F44336',
};

function getRankColor(rank: number | null): string {
  if (rank === null || rank > 20) return RANK_COLORS.notRanking;
  if (rank <= 3) return RANK_COLORS.top3;
  if (rank <= 10) return RANK_COLORS.top10;
  return RANK_COLORS.top20;
}

function getRankLabel(rank: number | null): string {
  if (rank === null) return '20+';
  if (rank > 20) return '20+';
  return String(rank);
}

function createMarkerIcon(rank: number | null): google.maps.Symbol {
  const color = getRankColor(rank);
  return {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: 0.9,
    strokeColor: '#fff',
    strokeWeight: 2,
    scale: 16,
  };
}

export const RankingMap: React.FC<RankingMapProps> = ({
  scan,
  centerLat,
  centerLng,
  loading,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const centerMarkerRef = useRef<google.maps.Marker | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    centerMarkerRef.current?.setMap(null);
    centerMarkerRef.current = null;
    infoWindowRef.current?.close();
  }, []);

  // Initialize map
  useEffect(() => {
    let mounted = true;

    const initMap = async () => {
      try {
        await loadGoogleMapsScript();
      } catch {
        // loadGoogleMapsScript may fail if key is not set, but Google Maps
        // might already be loaded by another component. Check before giving up.
      }

      if (!mounted || !mapRef.current) return;

      if (!window.google?.maps) {
        setMapError('Google Maps API key is not configured. Set VITE_GOOGLE_MAPS_API_KEY in your environment.');
        return;
      }

      try {
        const map = new google.maps.Map(mapRef.current, {
          center: { lat: centerLat, lng: centerLng },
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            {
              featureType: 'poi',
              stylers: [{ visibility: 'off' }],
            },
          ],
        });

        infoWindowRef.current = new google.maps.InfoWindow();
        setMapInstance(map);
        setMapError(null);
      } catch (error) {
        console.error('Failed to initialize Google Maps:', error);
        setMapError('Failed to initialize Google Maps.');
      }
    };

    initMap();

    return () => {
      mounted = false;
      clearMarkers();
      setMapInstance(null);
    };
  }, [centerLat, centerLng, clearMarkers]);

  // Scan data can arrive before the async map initialization finishes.
  // React state ensures pins render when either the map or the scan is ready.
  useEffect(() => {
    const map = mapInstance;
    if (!map) return;

    clearMarkers();

    if (!scan || !scan.grid_points?.length) return;

    // Add center marker (business location)
    const center = new google.maps.Marker({
      position: { lat: scan.center_lat, lng: scan.center_lng },
      map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: '#1976D2',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 3,
        scale: 10,
      },
      zIndex: 1000,
      title: 'Business Location',
    });
    centerMarkerRef.current = center;

    // Add grid point markers
    scan.grid_points.forEach((point: RankingGridPoint) => {
      const marker = new google.maps.Marker({
        position: { lat: point.lat, lng: point.lng },
        map,
        icon: createMarkerIcon(point.rank),
        label: {
          text: getRankLabel(point.rank),
          color: '#fff',
          fontSize: '11px',
          fontWeight: 'bold',
        },
        zIndex: point.rank != null ? 100 - point.rank : 1,
      });

      marker.addListener('click', () => {
        const infoWindow = infoWindowRef.current;
        if (!infoWindow) return;

        const content = buildInfoWindowContent(point);
        infoWindow.setContent(content);
        infoWindow.open(map, marker);
      });

      markersRef.current.push(marker);
    });

    // Fit bounds to show all markers
    const bounds = new google.maps.LatLngBounds();
    scan.grid_points.forEach((p: RankingGridPoint) => {
      bounds.extend({ lat: p.lat, lng: p.lng });
    });
    map.fitBounds(bounds, 40);
  }, [mapInstance, scan, clearMarkers]);

  const overlayStyles = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'md',
    zIndex: 1,
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Map div — ALWAYS in the DOM to prevent Google Maps instance orphaning */}
      <Box
        ref={mapRef}
        sx={{
          height: 500,
          borderRadius: 'md',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'neutral.200',
        }}
      />

      {/* Loading overlay */}
      {loading && (
        <Box sx={{ ...overlayStyles, bgcolor: 'rgba(255,255,255,0.7)' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error overlay */}
      {mapError && (
        <Box sx={{ ...overlayStyles, bgcolor: 'rgba(255,255,255,0.9)' }}>
          <Alert color="warning">{mapError}</Alert>
        </Box>
      )}

      {/* Empty state overlay */}
      {!scan && !loading && !mapError && (
        <Box sx={{ ...overlayStyles, bgcolor: 'rgba(255,255,255,0.8)' }}>
          <Typography level="body-md" color="neutral">
            Select a keyword and run a scan to see ranking data
          </Typography>
        </Box>
      )}
    </Box>
  );
};

function buildInfoWindowContent(point: RankingGridPoint): string {
  const rankDisplay = point.rank != null ? `#${point.rank}` : 'Not ranking';
  const color = getRankColor(point.rank);

  let html = `
    <div style="font-family: sans-serif; min-width: 200px; padding: 4px;">
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
        <span style="
          display: inline-flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 50%;
          background: ${color}; color: #fff; font-weight: bold; font-size: 14px;
        ">${point.rank != null ? point.rank : '20+'}</span>
        <strong style="font-size: 14px;">${rankDisplay}</strong>
      </div>
  `;

  if (point.top_results && point.top_results.length > 0) {
    html += '<div style="font-size: 12px;">';
    point.top_results.forEach((result) => {
      const gbpUrl = result.place_id
        ? `https://www.google.com/maps/place/?q=place_id:${result.place_id}`
        : `https://www.google.com/maps/search/${encodeURIComponent(result.name)}`;
      html += `
        <div style="display: flex; gap: 6px; padding: 2px 0; align-items: center;">
          <span style="
            display: inline-flex; align-items: center; justify-content: center;
            min-width: 20px; height: 20px; border-radius: 50%;
            background: ${result.position <= 3 ? '#4CAF50' : result.position <= 10 ? '#FFC107' : '#FF9800'};
            color: #fff; font-size: 10px; font-weight: bold;
          ">${result.position}</span>
          <a href="${gbpUrl}" target="_blank" rel="noopener noreferrer" style="
            color: #1a73e8; text-decoration: none;
          " onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">${result.name}</a>
        </div>
      `;
    });
    html += '</div>';
  }

  html += `
    <div style="font-size: 10px; color: #999; margin-top: 6px;">
      ${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}
    </div>
  </div>`;

  return html;
}
